import { Component, Input, OnChanges, OnInit, SimpleChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SchemaServiceResolver } from './schema-service.resolver';
import { Subject, takeUntil } from 'rxjs';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { CommonModule } from '@angular/common';
import { getCode } from 'app/utils/method';
import { GenericActiveLookupService } from './select-schema.service';

@Component({
  selector: 'app-select-schema',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule
  ],
  templateUrl: './select-schema.component.html',
  styleUrl: './select-schema.component.scss'
})
export class SelectSchemaComponent implements OnInit, OnChanges, OnDestroy {

  getCode = getCode;

  @Input() schema: string = '';
  @Input() form!: FormGroup;
  @Input() isMultiple: boolean = false;

  originalGeneralList: any[] = [];
  generalList: any[] = [];

  searchGeneralCtrl = new FormControl('');

  private _onDestroy = new Subject<void>();

  displayValue: string = '';

  private unregisteredTimer: any = null;

  constructor(
    private configResolver: SchemaServiceResolver,
    private genericService: GenericActiveLookupService,
    private handlerMessage: HandlerMessageService
  ) {}

  onSelectionChange() {
    const value = this.form.get('defaultValue')?.value;

    if (this.isMultiple) {
      const normalized = Array.isArray(value)
        ? value.map(v => v?.toString())
        : value ? [value.toString()] : [];

      this.form.get('defaultValue')?.setValue(normalized, { emitEvent: false });

    } else {
      const normalized = value ? value.toString() : null;
      this.form.get('defaultValue')?.setValue(normalized, { emitEvent: false });
    }

    this.updateDisplayValue();
  }

  ngOnInit(): void {
    this.getList();

    this.searchGeneralCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((val) => this.getList(val || ''));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schema'] && !changes['schema'].firstChange) {
      this.searchGeneralCtrl.setValue('', { emitEvent: false });
      this.getList();
    }
  }

  ngOnDestroy(): void {
    this.clearUnregisteredTimer();
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private clearUnregisteredTimer(): void {
    if (this.unregisteredTimer) {
      clearTimeout(this.unregisteredTimer);
      this.unregisteredTimer = null;
    }
  }

  // Espera a que el usuario deje de escribir para no lanzar un toast por tecla
  private notifyUnregisteredSchema(): void {
    this.clearUnregisteredTimer();
    this.unregisteredTimer = setTimeout(() => {
      this.unregisteredTimer = null;
      this.handlerMessage.error({
        message: `El schema "${this.schema}" no está configurado, no se pueden cargar valores.`,
      });
    }, 1500);
  }

  compareFn = (a, b) => {
    return a?.toString() === b?.toString();
  };

  getList(search?: string): void {
    if (!this.schema) return;

    const config = this.configResolver.resolve(this.schema);

    if (!config) {
      this.generalList = [];
      this.originalGeneralList = [];
      this.displayValue = '';
      this.notifyUnregisteredSchema();
      return;
    }

    this.clearUnregisteredTimer();

    this.genericService.findAll(config, {
      currentPage: 1,
      limitPerPage: 50,
      search: search || ''
    })
    .subscribe({
      next: (resp) => {
        this.generalList = resp.data[0]?.data || [];
        this.originalGeneralList = [...this.generalList];

        const value = this.form.get('defaultValue')?.value;

        if (value) {
          if (this.isMultiple) {
            const normalized = Array.isArray(value)
              ? value.map(v => v.toString())
              : [value.toString()];

            this.form.get('defaultValue')?.setValue(normalized, { emitEvent: false });

          } else {
            this.form.get('defaultValue')?.setValue(value.toString(), { emitEvent: false });
          }
        }

        this.updateDisplayValue();
      },
      error: (err) => {
        this.handlerMessage.error({
          message: err?.error?.message || 'Error al traer registro',
        });
      }
    });
  }

  updateDisplayValue() {
    const value = this.form.get('defaultValue')?.value;

    if (!value || !this.originalGeneralList.length) {
      this.displayValue = '';
      return;
    }

    const idsArray = Array.isArray(value)
      ? value.map(v => v.toString())
      : [value.toString()];

    this.displayValue = this.originalGeneralList
      .filter(item => idsArray.includes(item._id.toString()))
      .map(item => item?.name ?? '')
      .join(', ');
  }
}